import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { QueryClient } from '@tanstack/react-query';
import { openDB } from 'idb';

// IndexedDB para mejorar rendimiento en cache
/**
 * An AsyncStorage persister for React Query that uses IndexedDB for storage.
 *
 * @remarks
 * This persister opens (or upgrades) an IndexedDB database named `"react-query"` with a single
 * object store called `"store"`. It implements the required `getItem`, `setItem`, and `removeItem`
 * methods to persist and retrieve serialized query cache data.
 *
 * - `getItem(key)`: Retrieves the stored value for the given key or returns `null` if not found.
 * - `setItem(key, value)`: Stores the value under the given key.
 * - `removeItem(key)`: Deletes the entry for the given key.
 *
 * Internally, it uses the `idb` library's `openDB` function to manage the database connection.
 *
 * @example
 * ```ts
 * import { QueryClient } from '@tanstack/react-query';
 * import { persistQueryClient } from '@tanstack/react-query-persist-client';
 *
 * const queryClient = new QueryClient();
 * persistQueryClient({
 *   queryClient,
 *   persister: _indexedDBPersister,
 * });
 * ```
 *
 * @public
 */
const _indexedDBPersister = createAsyncStoragePersister({
  storage: {
    getItem: async (key) => {
      const db = await openDB('react-query', 1, {
        upgrade(db) {
          db.createObjectStore('store');
        },
      });
      return (await db.get('store', key)) || null;
    },
    setItem: async (key, value) => {
      const db = await openDB('react-query', 1);
      await db.put('store', value, key);
    },
    removeItem: async (key) => {
      const db = await openDB('react-query', 1);
      await db.delete('store', key);
    },
  },
});

/**
 * Creates a QueryClient instance with default options for queries.
 * This configuration sets the garbage collection time, refetch behavior,
 * retry attempts, and stale time for cached queries.
 * @returns A configured QueryClient instance.
 * @public
 * @remarks
 * The `gcTime` is set to 24 hours, meaning cached queries will be garbage collected after this period.
 * The `refetchOnWindowFocus` is disabled to prevent unnecessary refetching when the window gains focus.
 * The `retry` option is set to 1, allowing each query to retry once on failure.
 * The `staleTime` is set to 2 minutes, meaning queries will be considered fresh for this duration.
 *
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 1000 * 60 * 60 * 24, // 24 horas antes de limpiar cache
      refetchOnWindowFocus: false,
      retry: 1, // Reintentar solo una vez
      staleTime: 2 * 60 * 1000, // 2 minutos
    },
  },
});

// Export const persister = indexedDBPersister;

export const persister = createSyncStoragePersister({
  storage: window.localStorage,
});
